import axios from "axios";
import { store } from "../store";
import { logout, setLoading } from "../store/authSlice";

const API_URL =
  process.env.NODE_ENV === "production"
    ? "https://api.colefaxjobcards.co.za"
    : "http://localhost:8002";

// Create a separate instance for CSRF requests
const csrfAxios = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    Accept: "application/json",
    "X-Requested-With": "XMLHttpRequest",
  },
});

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
    "X-Requested-With": "XMLHttpRequest",
  },
});

// Function to get CSRF token
const getCsrfToken = async () => {
  try {
    await csrfAxios.get("/sanctum/csrf-cookie");
  } catch (error) {
    console.error("Error fetching CSRF token:", error);
  }
};

// Request interceptor for API calls
api.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Only get CSRF token for mutations (POST, PUT, DELETE, PATCH)
    const requiresCsrf = ["post", "put", "delete", "patch"].includes(
      config.method?.toLowerCase() || ""
    );
    if (requiresCsrf && !document.cookie.includes("XSRF-TOKEN")) {
      await getCsrfToken();
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for API calls
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      store.dispatch(logout());
      store.dispatch(setLoading(false));
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    // Handle CSRF token expiration
    if (error.response?.status === 419) {
      await getCsrfToken();
      // Retry the original request
      return api(error.config);
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  login: async (credentials: { email: string; password: string }) => {
    try {
      await getCsrfToken();
      const response = await api.post("/login", credentials);
      // Store token after successful login
      const token = response.data.token;
      if (token) {
        localStorage.setItem("token", token);
      }
      return response;
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  },


  logout: async () => {
    try {
      await api.post("/logout");
      localStorage.removeItem("token");
    } catch (error) {
      console.error("Logout error:", error);
      throw error;
    }
  },

  updateProfile: async (userData: {
    name: string;
    email: string;
    current_password?: string;
    new_password?: string;
    new_password_confirmation?: string;
  }) => {
    try {
      await getCsrfToken();
      return await api.post("/profile/update", userData);
    } catch (error) {
      console.error("Profile update error:", error);
      throw error;
    }
  },

  getUser: async () => {
    try {
      return await api.get("/user");
    } catch (error) {
      console.error("Get user error:", error);
      throw error;
    }
  },
};

export default api;
